import { useContext } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

import type { MaskitoOptions } from '@maskito/core';
import { useMaskito } from '@maskito/react';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';
import { ReadOnlyField } from '@/lib/pages/forms/components/ReadOnlyField';
import { withMaskitoField } from '@/lib/utils/with-maskito-register';

// Formats zip numbers as '12345' or '12345-1234'
const zipMask: MaskitoOptions = {
  mask: [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
};

export function ZipInput<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  orientation = 'horizontal',
  type = 'text',
  required = false,
  readOnly,
  nativeRequired = false,
}: {
  control: Control<T>;
  name: Path<T> | string;
  label: string;
  placeholder?: string;
  orientation?: 'horizontal' | 'vertical';
  type?: string;
  required?: boolean;
  readOnly?: boolean;
  nativeRequired?: boolean;
}) {
  const { editMode } = useContext(AgentAuthorityContext);
  const maskitoRef = useMaskito({ options: zipMask });
  return (
    <FormField
      control={control}
      name={name as Path<T>}
      render={({ field }) => {
        if (!editMode || readOnly) {
          return <ReadOnlyField label={label} value={field.value} />;
        } else {
          return (
            <FormItem
              className={`flex ${orientation === 'horizontal' ? 'items-center gap-4' : 'w-full flex-col'}`}
            >
              <FormLabel
                className={`${orientation === 'horizontal' ? 'w-1/5' : 'mb-2'} font-semibold leading-5`}
              >
                {label}
                {required || nativeRequired ? (
                  <span className="text-red-400">*</span>
                ) : null}
              </FormLabel>
              <div
                className={`${orientation === 'horizontal' ? 'w-2/5' : ''} !mt-0`}
              >
                <FormControl>
                  <Input
                    type={type}
                    placeholder={placeholder}
                    required={nativeRequired}
                    pattern="^\d{5}(-\d{4})?$"
                    {...withMaskitoField(field, maskitoRef)}
                  />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          );
        }
      }}
    />
  );
}
